import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNotices = () => {
  const { token } = useAuth();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', is_important: false, target_audience: 'all', expires_at: '' });

  useEffect(() => { fetchNotices(); }, []);

  const fetchNotices = async () => {
    try { const res = await axios.get(`${API_URL}/api/notices`); setNotices(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/notices`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Notice published!'); setDialogOpen(false); setFormData({ title: '', content: '', is_important: false, target_audience: 'all', expires_at: '' }); fetchNotices();
    } catch (e) { toast.error('Failed to publish'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notice?')) return;
    try { await axios.delete(`${API_URL}/api/notices/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted!'); fetchNotices(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <AdminLayout title="Notices">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Publish notices for students.</p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button data-testid="add-notice-btn"><Plus className="w-4 h-4 mr-2" /> Publish Notice</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Publish Notice</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Content</Label><Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} rows={4} required /></div>
              <div className="space-y-2">
                <Label>Expiry Date <span className="text-muted-foreground text-xs">(optional — notice hides after this date)</span></Label>
                <Input
                  type="date"
                  value={formData.expires_at}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_important} onCheckedChange={(c) => setFormData({...formData, is_important: c})} /><Label>Mark as Important</Label></div>
              <Button type="submit" className="w-full">Publish</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (<div className="space-y-4">{[1,2,3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-20"></CardContent></Card>)}</div>) : notices.length > 0 ? (
        <div className="space-y-4">
          {notices.map((notice) => (
            <Card key={notice.id} data-testid={`notice-${notice.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${notice.is_important ? 'bg-red-100' : 'bg-primary/10'}`}>
                    <Bell className={`w-5 h-5 ${notice.is_important ? 'text-red-600' : 'text-primary'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><h4 className="font-semibold">{notice.title}</h4>{notice.is_important && <Badge variant="destructive">Important</Badge>}</div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{notice.content}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <p className="text-xs text-muted-foreground">Published: {notice.published_at?.split('T')[0]}</p>
                      {notice.expires_at && (
                        <p className="text-xs text-orange-500 font-medium">Expires: {notice.expires_at?.split('T')[0]}</p>
                      )}
                    </div>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => handleDelete(notice.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (<Card><CardContent className="py-16 text-center"><Bell className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No Notices</h3></CardContent></Card>)}
    </AdminLayout>
  );
};

export default AdminNotices;
